import { useState, useEffect, useRef, useCallback } from "react";
import { theme, fontDisplay, fontBody, fontMono, ALL_SOURCES, SOURCE_META, WORK_TYPE_META } from "./theme";
import {
  IconCheck, IconSpinner, IconChevronDown, IconMapPin, IconGem, IconKey,
  IconSparkle, IconClock, IconLogOut, Pill, SourcePill,
} from "./icons";
import { TagInput, JobCard, SkippedRow } from "./components";

/* ── API base ───────────────────────────────────────────────────────
   The backend serves frontend + API from the same domain in production
   (DEPLOY_ENV=production → BASE_URL=FRONTEND_URL=crowncraftinnovations.in).
   So: same-origin in prod, Flask dev server on :5001 locally. No env
   file needed, and it still works if the VPS domain ever changes. ── */
const API = (() => {
  if (typeof window !== "undefined" && window.location && window.location.hostname) {
    const h = window.location.hostname;
    if (h !== "localhost" && h !== "127.0.0.1:5001") return window.location.origin;
  }
  return "http://127.0.0.1:5001";
})();

const TOKEN_KEY = "tt_session_token";
const EXPIRES_KEY = "tt_session_expires";
const RAZORPAY_SRC = "https://checkout.razorpay.com/v1/checkout.js";

/* ─── A few small local icons (kept here, not in icons.js, so this file
   stays a drop-in replacement with no other file dependencies) ──── */
function IconUsers({ size = 16, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}
function IconShield({ size = 16, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}
function IconAlertTriangle({ size = 16, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}
function IconArrowLeft({ size = 14, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  );
}
function IconTrash({ size = 13, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1.5 14a2 2 0 0 1-2 1.8H8.5a2 2 0 0 1-2-1.8L5 6" />
      <path d="M10 11v5M14 11v5M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
  );
}

/* ─── Shared inline-style fragments for the auth/admin/billing forms —
   factored out once instead of re-typed on every input (the original
   file repeated this block per-field; with 3 auth modes that gets
   noisy fast). Pixel-identical to the original login screen. ────── */
const labelStyle = { fontSize: 11, fontWeight: 600, color: theme.textDim, letterSpacing: "1.2px", textTransform: "uppercase", display: "block", marginBottom: 8, fontFamily: fontMono };
const inputStyle = { width: "100%", padding: "12px 14px", border: `1px solid ${theme.border}`, borderRadius: 10, fontSize: 14, boxSizing: "border-box", background: theme.bgInset, color: theme.text };
const linkBtnStyle = { background: "none", border: "none", color: theme.gold, fontWeight: 700, fontSize: 13, cursor: "pointer", padding: 0, fontFamily: fontBody };
const errBoxStyle = { background: theme.garnetBg, border: `1px solid rgba(243,113,113,0.3)`, color: theme.garnet, padding: "12px 14px", borderRadius: 10, marginBottom: 22, fontSize: 13, fontWeight: 500 };
const infoBoxStyle = { background: theme.emeraldBg, border: `1px solid rgba(52,211,153,0.3)`, color: theme.emerald, padding: "12px 14px", borderRadius: 10, marginBottom: 22, fontSize: 13, fontWeight: 500 };
function primaryBtnStyle(loading) {
  return {
    width: "100%", padding: "13px", background: loading ? "rgba(201,164,104,0.4)" : theme.gold,
    color: "#1a1306", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: loading ? "wait" : "pointer",
    display: "flex", alignItems: "center", justifyContent: "center", gap: 8, letterSpacing: "0.2px",
  };
}

/* ─── Formatting / access helpers ─────────────────────────────────── */
function fmtDuration(ms) {
  if (ms == null || ms <= 0) return "0m";
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}
function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}
function fmtINR(paise) {
  return `₹${Math.round((paise || 0) / 100).toLocaleString("en-IN")}`;
}
function accessStatusMeta(access) {
  const a = access || {};
  if (!a.ok) return { text: "No access", color: theme.garnet, bg: theme.garnetBg };
  if (a.reason === "admin") return { text: "Admin", color: theme.gold, bg: theme.goldSoft };
  if (a.is_demo) return { text: "Demo", color: theme.amber, bg: theme.amberBg };
  if (a.reason === "admin_grant") return { text: "Granted", color: theme.sapphire, bg: theme.sapphireBg };
  if (a.reason === "subscription") return { text: "Subscribed", color: theme.emerald, bg: theme.emeraldBg };
  return { text: "Unknown", color: theme.textFaint, bg: theme.bgInset };
}
function accessExpiredMessage(access) {
  if (!access) return "Your access has ended — subscribe to keep scanning.";
  if (access.reason === "admin_grant_expired") return "Your granted access has expired — subscribe to keep scanning.";
  if (access.demo_expires_at) return "Your free demo has ended — subscribe to keep scanning.";
  return "Subscribe to start scanning.";
}

/* ─── Razorpay checkout.js loader — injected once, reused after ──── */
function loadRazorpayScript() {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) { resolve(); return; }
    const existing = document.getElementById("razorpay-checkout-js");
    if (existing) { existing.addEventListener("load", () => resolve()); return; }
    const s = document.createElement("script");
    s.id = "razorpay-checkout-js";
    s.src = RAZORPAY_SRC;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Could not load the payment SDK"));
    document.body.appendChild(s);
  });
}

/* ════════════════════════════════════════════════════════════════════
   GLOBAL STYLES — animation language for the whole app. A soft 220ms
   ease for UI state changes, a slower shimmering gold trace for the
   "live scan in progress" feeling (the page's signature motion).
   ════════════════════════════════════════════════════════════════════ */
function GlobalStyles() {
  return (
    <style>{`
      * { box-sizing: border-box; }
      html, body, #root { background: ${theme.bg}; margin: 0; }
      ::-webkit-scrollbar { width: 8px; height: 8px; }
      ::-webkit-scrollbar-track { background: transparent; }
      ::-webkit-scrollbar-thumb { background: rgba(201,164,104,0.18); border-radius: 4px; }
      ::-webkit-scrollbar-thumb:hover { background: rgba(201,164,104,0.32); }

      @keyframes tt-spin { to { transform: rotate(360deg); } }
      .tt-spin { animation: tt-spin 0.9s linear infinite; }

      @keyframes tt-fade-up { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
      .tt-fade-up { animation: tt-fade-up 0.5s cubic-bezier(.16,1,.3,1) both; }

      @keyframes tt-slide-in { from { opacity: 0; transform: translateY(-10px) scale(0.99); } to { opacity: 1; transform: translateY(0) scale(1); } }
      .tt-slide-in { animation: tt-slide-in 0.45s cubic-bezier(.16,1,.3,1) both; }

      @keyframes tt-shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
      .tt-shimmer-border { position: relative; }
      .tt-shimmer-border::before {
        content: ""; position: absolute; inset: -1px; border-radius: inherit;
        background: linear-gradient(90deg, transparent 0%, rgba(201,164,104,0) 35%, rgba(201,164,104,0.95) 50%, rgba(201,164,104,0) 65%, transparent 100%);
        background-size: 250% 100%; animation: tt-shimmer 2.2s linear infinite;
        -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
        mask-composite: exclude; -webkit-mask-composite: xor; padding: 1px; pointer-events: none;
      }

      @keyframes tt-pulse-dot { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.35; transform: scale(0.78); } }
      .tt-pulse-dot { animation: tt-pulse-dot 1.4s ease-in-out infinite; }

      @keyframes tt-gold-flash { 0% { box-shadow: 0 0 0 0 rgba(201,164,104,0.5); } 100% { box-shadow: 0 0 0 14px rgba(201,164,104,0); } }
      .tt-gold-flash { animation: tt-gold-flash 1.1s cubic-bezier(.16,1,.3,1) both; }

      @keyframes tt-count-in { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
      .tt-count-in { animation: tt-count-in 0.35s ease both; }

      @keyframes tt-rotate-slow { to { transform: rotate(360deg); } }

      input::placeholder, textarea::placeholder { color: ${theme.textFaint}; }
      input:focus, select:focus, textarea:focus {
        outline: none; border-color: ${theme.borderHi} !important;
        box-shadow: 0 0 0 3px rgba(201,164,104,0.12);
      }
      button { font-family: ${fontBody}; }

      .tt-hover-lift { transition: transform 0.22s cubic-bezier(.16,1,.3,1), box-shadow 0.22s ease, border-color 0.22s ease, background 0.22s ease; }
      .tt-hover-lift:hover { transform: translateY(-2px); }

      .tt-press { transition: transform 0.12s ease; }
      .tt-press:active { transform: scale(0.97); }

      .tt-logo-btn { cursor: pointer; transition: opacity 0.2s ease; background: none; border: none; padding: 0; }
      .tt-logo-btn:hover { opacity: 0.78; }

      .tt-tab-btn { transition: all 0.22s cubic-bezier(.16,1,.3,1); }
    `}</style>
  );
}

/* ─── Session persistence (refresh-safe, 1hr hard expiry) ───────────── */
function saveSession(token, expiresAtSeconds) {
  try {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(EXPIRES_KEY, String(expiresAtSeconds * 1000));
  } catch {}
}
function clearSessionStorage() {
  try { localStorage.removeItem(TOKEN_KEY); localStorage.removeItem(EXPIRES_KEY); } catch {}
}
function readSession() {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    const expiresAt = Number(localStorage.getItem(EXPIRES_KEY) || 0);
    if (!token || !expiresAt || Date.now() > expiresAt) return null;
    return { token, expiresAt };
  } catch { return null; }
}

/* ════════════════════════════════════════════════════════════════════
   MAIN APP
   ════════════════════════════════════════════════════════════════════ */
export default function App() {
  // Auth
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [sessionExpiresAt, setSessionExpiresAt] = useState(null);
  const [restoringSession, setRestoringSession] = useState(true);
  const [access, setAccess] = useState(null);
  const [nowTick, setNowTick] = useState(Date.now());

  const [authMode, setAuthMode] = useState("signin"); // signin | signup | forgot
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [authErr, setAuthErr] = useState("");
  const [authInfo, setAuthInfo] = useState("");
  const [authLoading, setAuthLoading] = useState(false);

  // Sign up (OTP-gated)
  const [signup, setSignup] = useState({
    step: "form", name: "", email: "", password: "", confirm: "",
    otp: "", err: "", info: "", loading: false,
  });
  const patchSignup = (p) => setSignup(s => ({ ...s, ...p }));

  // Forgot password (OTP-gated)
  const [forgot, setForgot] = useState({
    step: "email", email: "", otp: "", token: "", password: "", confirm: "",
    err: "", info: "", loading: false,
  });
  const patchForgot = (p) => setForgot(s => ({ ...s, ...p }));

  // Prefs
  const [prefs, setPrefs] = useState({
    roles: [], cities: [], sources: ["LinkedIn","Naukri","Indeed"],
    frequency: "daily", custom_times: [], min_exp: 0, max_exp: 5,
    work_type: "any", notify_email: true,
    job_age_days: 30, job_age_minutes: 0,
    only_early_applicant: false, easy_apply_only: false,
    gemini_api_key: "", has_gemini_key: false,
  });
  const [prefsDirty, setPrefsDirty] = useState(false);
  const [prefsSaving, setPrefsSaving] = useState(false);
  const [prefsMsg, setPrefsMsg] = useState("");
  const [geminiTestState, setGeminiTestState] = useState(""); // "", "testing", "ok", "fail"
  const [geminiTestMsg, setGeminiTestMsg] = useState("");
  const [showGeminiKey, setShowGeminiKey] = useState(false);

  // Suggestions
  const [roleSugg, setRoleSugg] = useState([]);
  const [citySugg, setCitySugg] = useState([]);
  const roleDebounceRef = useRef(null);
  const cityDebounceRef = useRef(null);

  // Jobs & scan
  const [jobs, setJobs] = useState([]);
  const [newJobKeys, setNewJobKeys] = useState(new Set());
  const [skipped, setSkipped] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [paused, setPaused] = useState(false);
  const [scanDone, setScanDone] = useState(false);
  const [scanStatus, setScanStatus] = useState("");
  const [currentSource, setCurrentSource] = useState("");
  const [liveCount, setLiveCount] = useState(0);
  const [skipCount, setSkipCount] = useState(0);
  const [sourceDone, setSourceDone] = useState([]);
  const [emailSent, setEmailSent] = useState(false);
  const [searchedRoles, setSearchedRoles] = useState([]);

  // Scan-time role/city override
  const [scanRoles, setScanRoles] = useState([]);
  const [scanCities, setScanCities] = useState([]);

  // UI
  const [activeTab, setActiveTab] = useState("scan");
  const [filterSource, setFilterSource] = useState("all");
  const [filterWorkType, setFilterWorkType] = useState("all");
  const [filterEasy, setFilterEasy] = useState(false);
  const [filterRecruiter, setFilterRecruiter] = useState(false);
  const [showSkipped, setShowSkipped] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [customFreqTime, setCustomFreqTime] = useState("07:00");

  // Billing
  const [billingPlans, setBillingPlans] = useState(null);
  const [billingLoadingPlan, setBillingLoadingPlan] = useState("");
  const [billingErr, setBillingErr] = useState("");
  const [billingMsg, setBillingMsg] = useState("");
  const [paymentHistory, setPaymentHistory] = useState([]);

  // Admin
  const [adminStats, setAdminStats] = useState(null);
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminPayments, setAdminPayments] = useState([]);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminSearch, setAdminSearch] = useState("");
  const [adminMsg, setAdminMsg] = useState("");
  const [grantTarget, setGrantTarget] = useState(null);
  const [grantDays, setGrantDays] = useState(30);
  const [grantLoading, setGrantLoading] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUserForm, setNewUserForm] = useState({ name: "", email: "", password: "" });
  const [newUserErr, setNewUserErr] = useState("");
  const [newUserLoading, setNewUserLoading] = useState(false);

  const esRef = useRef(null);
  const idleLogoutTimerRef = useRef(null);

  /* ── Auth header helper ─────────────────────────────────────────── */
  const authHeaders = useCallback((extra = {}) => ({
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  }), [token]);

  /* ── Restore session on load / refresh ──────────────────────────── */
  useEffect(() => {
    const s = readSession();
    if (!s) { setRestoringSession(false); return; }
    fetch(`${API}/api/auth/session`, { headers: { Authorization: `Bearer ${s.token}` } })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(d => {
        if (d.status === "success") {
          setUser(d.user);
          setToken(d.token);
          setSessionExpiresAt(d.expires_at * 1000);
          setAccess(d.access || null);
          saveSession(d.token, d.expires_at);
          if (d.preferences) {
            setPrefs(p => ({ ...p, ...d.preferences }));
            setScanRoles(d.preferences.roles || []);
            setScanCities(d.preferences.cities || []);
          }
        } else {
          clearSessionStorage();
        }
      })
      .catch(() => clearSessionStorage())
      .finally(() => setRestoringSession(false));
  }, []);

  /* ── Hard 1-hour auto-logout, ticking against the real expiry time ── */
  useEffect(() => {
    if (!sessionExpiresAt) return;
    const tick = () => {
      if (Date.now() > sessionExpiresAt) {
        handleLogout(true);
      }
    };
    idleLogoutTimerRef.current = setInterval(tick, 5001);
    return () => clearInterval(idleLogoutTimerRef.current);
  }, [sessionExpiresAt]);

  /* ── Lightweight re-render tick so the demo/session countdowns
     actually count down instead of freezing between unrelated
     re-renders ───────────────────────────────────────────────────── */
  useEffect(() => {
    if (!user) return;
    const id = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(id);
  }, [user]);

  /* Load jobs on login */
  const loadJobs = useCallback((uid) => {
    fetch(`${API}/api/jobs/${uid}`).then(r => r.json()).then(d => {
      if (d.jobs) setJobs(d.jobs);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      loadJobs(user.id);
      setScanRoles(prefs.roles);
      setScanCities(prefs.cities);
    }
  }, [user]);

  useEffect(() => {
    if (!scanning) {
      setScanRoles(prefs.roles);
      setScanCities(prefs.cities);
    }
  }, [prefs.roles, prefs.cities]);

  /* ── Debounced role/city autosuggest — fixes "no auto suggestion" ── */
  const queryRoleSuggestions = useCallback((q) => {
    clearTimeout(roleDebounceRef.current);
    roleDebounceRef.current = setTimeout(() => {
      fetch(`${API}/api/roles/suggest?q=${encodeURIComponent(q)}`)
        .then(r => r.json()).then(d => setRoleSugg(d.roles || [])).catch(() => {});
    }, 180);
  }, []);
  const queryCitySuggestions = useCallback((q) => {
    clearTimeout(cityDebounceRef.current);
    cityDebounceRef.current = setTimeout(() => {
      fetch(`${API}/api/cities?q=${encodeURIComponent(q)}`)
        .then(r => r.json()).then(d => setCitySugg(d.cities || [])).catch(() => {});
    }, 180);
  }, []);

  // initial suggestion load
  useEffect(() => {
    queryRoleSuggestions("");
    queryCitySuggestions("");
  }, []);

  /* ── Login ──────────────────────────────────────────────────────── */
  const handleLogin = async () => {
    setAuthErr(""); setAuthLoading(true);
    try {
      const r = await fetch(`${API}/api/auth/login`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password: pwd }),
      });
      const d = await r.json();
      if (d.status === "success") {
        setUser(d.user);
        setToken(d.token);
        setSessionExpiresAt(d.expires_at * 1000);
        setAccess(d.access || null);
        saveSession(d.token, d.expires_at);
        if (d.preferences) {
          setPrefs(p => ({ ...p, ...d.preferences }));
          setScanRoles(d.preferences.roles || []);
          setScanCities(d.preferences.cities || []);
        }
      } else {
        setAuthErr(d.message || "Invalid credentials");
      }
    } catch { setAuthErr("Could not reach the server"); }
    setAuthLoading(false);
  };

  const handleLogout = (expired = false) => {
    if (token) fetch(`${API}/api/auth/logout`, { method: "POST", headers: authHeaders() }).catch(() => {});
    clearSessionStorage();
    setUser(null); setToken(null); setSessionExpiresAt(null); setAccess(null);
    setJobs([]);
    setActiveTab("scan");
    setAuthMode("signin");
    esRef.current?.close();
    if (expired) setAuthErr("Your session expired after 1 hour — please sign in again.");
  };

  /* ── Sign up (send code → verify → register) ──────────────────────── */
  const signupSendOtp = async () => {
    patchSignup({ err: "", info: "" });
    if (!signup.name.trim() || !signup.email.trim() || !signup.password) {
      patchSignup({ err: "Fill in your name, email, and password" }); return;
    }
    if (signup.password.length < 6) { patchSignup({ err: "Password must be at least 6 characters" }); return; }
    if (signup.password !== signup.confirm) { patchSignup({ err: "Passwords don't match" }); return; }
    patchSignup({ loading: true });
    try {
      const r = await fetch(`${API}/api/auth/send-otp`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: signup.email.trim().toLowerCase(), purpose: "signup" }),
      });
      const d = await r.json();
      if (d.status === "success") {
        patchSignup({ step: "otp", loading: false, info: d.message || "Code sent" });
      } else {
        patchSignup({ err: d.message || "Could not send code", loading: false });
      }
    } catch { patchSignup({ err: "Could not reach the server", loading: false }); }
  };

  const signupResend = async () => {
    patchSignup({ err: "", info: "" });
    try {
      const r = await fetch(`${API}/api/auth/send-otp`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: signup.email.trim().toLowerCase(), purpose: "signup" }),
      });
      const d = await r.json();
      patchSignup(d.status === "success" ? { info: "New code sent" } : { err: d.message || "Could not resend code" });
    } catch { patchSignup({ err: "Could not reach the server" }); }
  };

  const signupVerifyAndRegister = async () => {
    patchSignup({ err: "", info: "" });
    if (!signup.otp.trim()) { patchSignup({ err: "Enter the code from your email" }); return; }
    patchSignup({ loading: true });
    try {
      const vr = await fetch(`${API}/api/auth/verify-otp`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: signup.email.trim().toLowerCase(), otp: signup.otp.trim(), purpose: "signup" }),
      });
      const vd = await vr.json();
      if (vd.status !== "success") {
        patchSignup({ err: vd.message || "Invalid or expired code", loading: false }); return;
      }
      const rr = await fetch(`${API}/api/auth/register`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: signup.email.trim().toLowerCase(), name: signup.name.trim(),
          password: signup.password, verify_token: vd.verify_token,
        }),
      });
      const rd = await rr.json();
      if (rd.status === "success") {
        setUser(rd.user);
        setToken(rd.token);
        setSessionExpiresAt(rd.expires_at * 1000);
        setAccess(rd.access || null);
        saveSession(rd.token, rd.expires_at);
        // register() doesn't echo preferences back — pull the freshly-created
        // defaults so local state matches the server exactly from the start.
        fetch(`${API}/api/users/${rd.user.id}`).then(r2 => r2.json()).then(ud => {
          if (ud.preferences) {
            setPrefs(p => ({ ...p, ...ud.preferences }));
            setScanRoles(ud.preferences.roles || []);
            setScanCities(ud.preferences.cities || []);
          }
        }).catch(() => {});
        setSignup({ step: "form", name: "", email: "", password: "", confirm: "", otp: "", err: "", info: "", loading: false });
      } else {
        patchSignup({ err: rd.message || "Could not create your account", loading: false });
      }
    } catch { patchSignup({ err: "Could not reach the server", loading: false }); }
  };

  /* ── Forgot password (send code → verify → set new password) ──────── */
  const forgotSendOtp = async () => {
    patchForgot({ err: "", info: "" });
    if (!forgot.email.trim()) { patchForgot({ err: "Enter your account email" }); return; }
    patchForgot({ loading: true });
    try {
      const r = await fetch(`${API}/api/auth/send-otp`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgot.email.trim().toLowerCase(), purpose: "reset" }),
      });
      const d = await r.json();
      if (d.status === "success") {
        patchForgot({ step: "otp", loading: false, info: d.message || "Code sent" });
      } else {
        patchForgot({ err: d.message || "Could not send code", loading: false });
      }
    } catch { patchForgot({ err: "Could not reach the server", loading: false }); }
  };

  const forgotResend = async () => {
    patchForgot({ err: "", info: "" });
    try {
      const r = await fetch(`${API}/api/auth/send-otp`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgot.email.trim().toLowerCase(), purpose: "reset" }),
      });
      const d = await r.json();
      patchForgot(d.status === "success" ? { info: "New code sent" } : { err: d.message || "Could not resend code" });
    } catch { patchForgot({ err: "Could not reach the server" }); }
  };

  const forgotVerifyOtp = async () => {
    patchForgot({ err: "", info: "" });
    if (!forgot.otp.trim()) { patchForgot({ err: "Enter the code from your email" }); return; }
    patchForgot({ loading: true });
    try {
      const r = await fetch(`${API}/api/auth/verify-otp`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgot.email.trim().toLowerCase(), otp: forgot.otp.trim(), purpose: "reset" }),
      });
      const d = await r.json();
      if (d.status === "success") {
        patchForgot({ step: "newpass", loading: false, token: d.verify_token });
      } else {
        patchForgot({ err: d.message || "Invalid or expired code", loading: false });
      }
    } catch { patchForgot({ err: "Could not reach the server", loading: false }); }
  };

  const forgotSubmitNewPassword = async () => {
    patchForgot({ err: "", info: "" });
    if (forgot.password.length < 6) { patchForgot({ err: "Password must be at least 6 characters" }); return; }
    if (forgot.password !== forgot.confirm) { patchForgot({ err: "Passwords don't match" }); return; }
    patchForgot({ loading: true });
    try {
      const r = await fetch(`${API}/api/auth/reset-password`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgot.email.trim().toLowerCase(), verify_token: forgot.token, password: forgot.password }),
      });
      const d = await r.json();
      if (d.status === "success") {
        setEmail(forgot.email.trim().toLowerCase());
        setAuthInfo("Password updated — sign in below");
        setAuthMode("signin");
        setForgot({ step: "email", email: "", otp: "", token: "", password: "", confirm: "", err: "", info: "", loading: false });
      } else {
        patchForgot({ err: d.message || "Could not reset password", loading: false });
      }
    } catch { patchForgot({ err: "Could not reach the server", loading: false }); }
  };

  /* ── Save prefs ─────────────────────────────────────────────────── */
  const savePrefs = async () => {
    setPrefsSaving(true);
    try {
      const r = await fetch(`${API}/api/users/${user.id}/preferences`, {
        method: "PUT", headers: authHeaders(),
        body: JSON.stringify(prefs),
      });
      const d = await r.json();
      setPrefsMsg(d.status === "success" ? "Preferences saved" : "Save failed");
      setPrefsDirty(false);
    } catch { setPrefsMsg("Save failed"); }
    setPrefsSaving(false);
    setTimeout(() => setPrefsMsg(""), 3000);
  };

  const updatePref = (key, val) => {
    setPrefs(p => ({ ...p, [key]: val }));
    setPrefsDirty(true);
  };

  const testGeminiKey = async () => {
    setGeminiTestState("testing"); setGeminiTestMsg("");
    try {
      const r = await fetch(`${API}/api/gemini/test`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ api_key: prefs.gemini_api_key }),
      });
      const d = await r.json();
      setGeminiTestState(d.status === "success" ? "ok" : "fail");
      setGeminiTestMsg(d.message || "");
    } catch {
      setGeminiTestState("fail");
      setGeminiTestMsg("Could not reach the server to verify the key");
    }
  };

  /* ── SSE ────────────────────────────────────────────────────────── */
  const startSSE = useCallback((uid) => {
    esRef.current?.close();
    const es = new EventSource(`${API}/api/scan/${uid}/stream`);
    esRef.current = es;

    es.onmessage = (e) => {
      try {
        const ev = JSON.parse(e.data);
        if (ev.type === "ping") return;

        if (ev.type === "start") {
          setScanStatus(`Searching ${(ev.sources || []).join(", ")}…`);
          setSourceDone([]); setScanDone(false); setEmailSent(false);
          setSearchedRoles(ev.roles || []);
        }
        if (ev.type === "source_start") {
          setCurrentSource(ev.source);
          setScanStatus(`Searching ${ev.source}…`);
        }
        if (ev.type === "source_done") {
          setSourceDone(p => [...p, ev.source]);
          setCurrentSource("");
        }
        if (ev.type === "source_error") {
          setScanStatus(`${ev.source} ran into an error`);
        }
        if (ev.type === "job") {
          const j = ev.job;
          setJobs(prev => prev.find(x => x.job_key === j.job_key) ? prev : [...prev, j]);
          setNewJobKeys(s => new Set([...s, j.job_key]));
          setLiveCount(ev.count || 0);
        }
        if (ev.type === "skipped") {
          setSkipped(p => [...p, { source: ev.source, title: ev.title, company: ev.company, reason: ev.reason }]);
          setSkipCount(ev.count || 0);
        }
        if (ev.type === "paused") { setPaused(true); setScanStatus("Scan paused"); }
        if (ev.type === "resumed") { setPaused(false); setScanStatus("Scan resumed…"); }
        if (ev.type === "done") {
          setScanning(false); setPaused(false); setScanDone(true);
          setEmailSent(ev.email_sent || false);
          setScanStatus(ev.stopped ? "Scan stopped" : `Complete — ${ev.total_new} new, ${ev.total_skip || 0} filtered`);
          loadJobs(uid);
          es.close();
        }
      } catch {}
    };
    es.onerror = () => {
      setScanStatus("Stream disconnected");
      es.close(); setScanning(false);
    };
  }, [loadJobs]);

  /* ── Start scan ─────────────────────────────────────────────────── */
  const startScan = async () => {
    if (access && access.ok === false) {
      setScanStatus("Your access has ended — head to Billing to keep scanning");
      setActiveTab("billing");
      return;
    }
    const roles  = scanRoles.length  ? scanRoles  : prefs.roles;
    const cities = scanCities.length ? scanCities : prefs.cities;

    if (!roles.length || !cities.length) {
      setScanStatus("Add at least one role and city before scanning");
      return;
    }
    if (!prefs.sources.length) {
      setScanStatus("Select at least one job portal in Settings");
      return;
    }
    setScanning(true); setPaused(false); setScanDone(false);
    setLiveCount(0); setSkipCount(0); setScanStatus("Starting scan…");
    setSourceDone([]); setSkipped([]); setNewJobKeys(new Set());

    startSSE(user.id);
    await new Promise(r => setTimeout(r, 300));

    try {
      const res = await fetch(`${API}/api/scan/${user.id}`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ roles, cities }),
      });
      const d = await res.json();
      if (d.status !== "started") {
        if (d.message === "access_expired") {
          if (d.access) setAccess(d.access);
          setScanStatus("Your access has ended — head to Billing to keep scanning");
          setActiveTab("billing");
        } else {
          setScanStatus(d.message || "Scan could not be started");
        }
        setScanning(false);
        esRef.current?.close();
      }
    } catch {
      setScanStatus("Cannot reach server");
      setScanning(false);
      esRef.current?.close();
    }
  };

  const pauseScan  = () => fetch(`${API}/api/scan/${user.id}/pause`,  { method: "POST" });
  const resumeScan = () => fetch(`${API}/api/scan/${user.id}/resume`, { method: "POST" });
  const stopScan   = async () => {
    await fetch(`${API}/api/scan/${user.id}/stop`, { method: "POST" });
    setScanning(false); setPaused(false); setScanDone(true);
    setScanStatus("Scan stopped");
    esRef.current?.close();
  };

  const clearJobs = async () => {
    if (!window.confirm("Clear all saved jobs? This can't be undone.")) return;
    await fetch(`${API}/api/jobs/${user.id}`, { method: "DELETE" });
    setJobs([]); setNewJobKeys(new Set());
  };

  /* ── Billing ────────────────────────────────────────────────────── */
  const loadBillingConfig = useCallback(() => {
    fetch(`${API}/api/config/public`).then(r => r.json())
      .then(d => setBillingPlans(d.plans || null)).catch(() => {});
  }, []);

  const loadPaymentHistory = useCallback(() => {
    if (!user) return;
    fetch(`${API}/api/payments/history/${user.id}`, { headers: authHeaders() })
      .then(r => r.json()).then(d => { if (d.status === "success") setPaymentHistory(d.payments || []); })
      .catch(() => {});
  }, [user, authHeaders]);

  useEffect(() => {
    if (activeTab === "billing" && user) {
      loadBillingConfig();
      loadPaymentHistory();
    }
  }, [activeTab, user, loadBillingConfig, loadPaymentHistory]);

  const startCheckout = async (planId) => {
    setBillingErr(""); setBillingMsg(""); setBillingLoadingPlan(planId);
    try {
      const orderRes = await fetch(`${API}/api/payments/create-order`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ plan: planId }),
      });
      const order = await orderRes.json();
      if (order.status !== "success") {
        setBillingErr(order.message || "Could not start checkout");
        setBillingLoadingPlan(""); return;
      }
      await loadRazorpayScript();
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: "TalentTracker",
        description: `${order.plan_label} subscription`,
        prefill: { name: order.prefill_name, email: order.prefill_email },
        theme: { color: "#c9a468" },
        handler: async (response) => {
          try {
            const vr = await fetch(`${API}/api/payments/verify`, {
              method: "POST", headers: authHeaders(),
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                plan: planId,
              }),
            });
            const vd = await vr.json();
            if (vd.status === "success") {
              setAccess(vd.access);
              setBillingMsg(vd.message || "Payment successful");
              loadPaymentHistory();
            } else {
              setBillingErr(vd.message || "Payment verification failed");
            }
          } catch {
            setBillingErr("Could not verify the payment — contact support if money was deducted");
          }
          setBillingLoadingPlan("");
        },
        modal: { ondismiss: () => setBillingLoadingPlan("") },
      });
      rzp.open();
    } catch (ex) {
      setBillingErr(ex.message || "Could not start checkout");
      setBillingLoadingPlan("");
    }
  };

  /* ── Admin ──────────────────────────────────────────────────────── */
  const loadAdminData = useCallback(() => {
    if (!user?.is_admin) return;
    setAdminLoading(true);
    Promise.all([
      fetch(`${API}/api/admin/stats`, { headers: authHeaders() }).then(r => r.json()).catch(() => null),
      fetch(`${API}/api/users`, { headers: authHeaders() }).then(r => r.json()).catch(() => null),
      fetch(`${API}/api/admin/payments`, { headers: authHeaders() }).then(r => r.json()).catch(() => null),
    ]).then(([stats, users, payments]) => {
      if (stats && stats.status === "success") setAdminStats(stats);
      if (users && users.status === "success") setAdminUsers(users.users || []);
      if (payments && payments.status === "success") setAdminPayments(payments.payments || []);
    }).finally(() => setAdminLoading(false));
  }, [user, authHeaders]);

  useEffect(() => {
    if (activeTab === "admin" && user?.is_admin) loadAdminData();
  }, [activeTab, user, loadAdminData]);

  const submitGrant = async (action) => {
    if (!grantTarget) return;
    setGrantLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/access/${grantTarget.id}`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ action, days: action === "grant" ? grantDays : 0 }),
      });
      const d = await r.json();
      if (d.status === "success") {
        setAdminMsg(action === "grant" ? `Access granted to ${grantTarget.name || grantTarget.email}` : `Access revoked for ${grantTarget.name || grantTarget.email}`);
        setGrantTarget(null);
        loadAdminData();
      } else {
        setAdminMsg(d.message || "Action failed");
      }
    } catch { setAdminMsg("Could not reach the server"); }
    setGrantLoading(false);
    setTimeout(() => setAdminMsg(""), 3500);
  };

  const submitNewUser = async () => {
    setNewUserErr("");
    if (!newUserForm.name.trim() || !newUserForm.email.trim() || !newUserForm.password) {
      setNewUserErr("Fill in name, email, and password"); return;
    }
    setNewUserLoading(true);
    try {
      const r = await fetch(`${API}/api/users`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify(newUserForm),
      });
      const d = await r.json();
      if (d.status === "success") {
        setShowAddUser(false);
        setNewUserForm({ name: "", email: "", password: "" });
        loadAdminData();
      } else {
        setNewUserErr(d.message || "Could not create user");
      }
    } catch { setNewUserErr("Could not reach the server"); }
    setNewUserLoading(false);
  };

  const deleteUserAdmin = async (u) => {
    if (!window.confirm(`Delete ${u.name || u.email}? This removes their jobs and preferences permanently.`)) return;
    try {
      await fetch(`${API}/api/users/${u.id}`, { method: "DELETE", headers: authHeaders() });
      loadAdminData();
    } catch {}
  };

  /* Filtered display jobs */
  const displayJobs = jobs.filter(j => {
    if (filterSource !== "all" && j.source !== filterSource) return false;
    if (filterWorkType !== "all" && j.work_type !== filterWorkType) return false;
    if (filterEasy && !j.is_easy_apply) return false;
    if (filterRecruiter && !j.is_recruiter_post) return false;
    if (searchQ) {
      const q = searchQ.toLowerCase();
      return (j.title || "").toLowerCase().includes(q) ||
             (j.company || "").toLowerCase().includes(q) ||
             (j.location || "").toLowerCase().includes(q);
    }
    return true;
  });

  const statsBySource = ALL_SOURCES.reduce((a, s) => ({ ...a, [s]: jobs.filter(j => j.source === s).length }), {});

  /* ════════════════════════ RENDER: loading/restoring ════════════════════════ */
  if (restoringSession) {
    return (
      <>
        <GlobalStyles />
        <div style={{ minHeight: "100vh", background: theme.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ color: theme.textDim, display: "flex", alignItems: "center", gap: 10, fontFamily: fontBody, fontSize: 13 }}>
            <IconSpinner size={16} color={theme.gold} /> Restoring your session…
          </div>
        </div>
      </>
    );
  }

  /* ════════════════════════ RENDER: auth (sign in / sign up / forgot) ════════════════════════ */
  if (!user) {
    return (
      <>
        <GlobalStyles />
        <div style={{
          minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
          background: `radial-gradient(ellipse at top, #14161d 0%, ${theme.bg} 60%)`,
          fontFamily: fontBody, position: "relative", overflow: "hidden",
        }}>
          <div style={{
            position: "absolute", width: 520, height: 520, borderRadius: "50%",
            background: "radial-gradient(circle, rgba(201,164,104,0.10) 0%, transparent 70%)",
            top: "-10%", left: "50%", transform: "translateX(-50%)", pointerEvents: "none",
          }} />

          <div key={authMode + (signup.step || "") + (forgot.step || "")} className="tt-fade-up" style={{
            background: theme.bgRaised, borderRadius: 18, padding: "46px 48px", width: 440,
            border: `1px solid ${theme.border}`,
            boxShadow: "0 30px 80px rgba(0,0,0,.55)", position: "relative", zIndex: 1,
          }}>
            <div style={{ marginBottom: 32, display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18, color: theme.gold }}>
                <IconGem size={26} />
                <span style={{ fontFamily: fontDisplay, fontSize: 13, fontWeight: 700, letterSpacing: "3px", textTransform: "uppercase", color: theme.goldText }}>
                  TalentTracker
                </span>
              </div>
              <h1 style={{ margin: 0, fontSize: 25, fontWeight: 700, color: theme.text, marginBottom: 8, letterSpacing: "-0.01em", fontFamily: fontDisplay }}>
                {authMode === "signin" && "Sign in"}
                {authMode === "signup" && (signup.step === "form" ? "Create your desk" : "Verify your email")}
                {authMode === "forgot" && (forgot.step === "email" ? "Reset password" : forgot.step === "otp" ? "Verify your email" : "Choose a new password")}
              </h1>
              <p style={{ margin: 0, color: theme.textDim, fontSize: 13.5 }}>
                {authMode === "signin" && "Access your private job search desk"}
                {authMode === "signup" && (signup.step === "form" ? "A few details to start your free demo" : `We sent a 6-digit code to ${signup.email}`)}
                {authMode === "forgot" && (forgot.step === "email" ? "We'll email you a code to verify it's you" : forgot.step === "otp" ? `Enter the code sent to ${forgot.email}` : "Make it at least 6 characters")}
              </p>
            </div>

            {/* SIGN IN */}
            {authMode === "signin" && (
              <>
                {authInfo && <div className="tt-slide-in" style={infoBoxStyle}>{authInfo}</div>}
                {authErr && <div className="tt-slide-in" style={errBoxStyle}>{authErr}</div>}

                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>Email</label>
                  <input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="you@example.com" style={inputStyle} />
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={labelStyle}>Password</label>
                  <input value={pwd} onChange={e => setPwd(e.target.value)} type="password" placeholder="••••••••"
                    onKeyDown={e => e.key === "Enter" && handleLogin()} style={inputStyle} />
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 22 }}>
                  <button onClick={() => { setAuthErr(""); setAuthInfo(""); setAuthMode("forgot"); }} style={linkBtnStyle}>Forgot password?</button>
                </div>
                <button onClick={handleLogin} disabled={authLoading} className="tt-press" style={primaryBtnStyle(authLoading)}>
                  {authLoading && <IconSpinner size={14} color="#1a1306" />}
                  {authLoading ? "Signing in…" : "Sign in"}
                </button>
                <div style={{ marginTop: 22, textAlign: "center", fontSize: 13, color: theme.textFaint }}>
                  New here?{" "}
                  <button onClick={() => { setAuthErr(""); setAuthInfo(""); setAuthMode("signup"); }} style={linkBtnStyle}>Create an account</button>
                </div>
              </>
            )}

            {/* SIGN UP */}
            {authMode === "signup" && (
              <>
                {signup.err && <div className="tt-slide-in" style={errBoxStyle}>{signup.err}</div>}
                {signup.info && signup.step === "otp" && <div className="tt-slide-in" style={infoBoxStyle}>{signup.info}</div>}

                {signup.step === "form" ? (
                  <>
                    <div style={{ marginBottom: 16 }}>
                      <label style={labelStyle}>Full name</label>
                      <input value={signup.name} onChange={e => patchSignup({ name: e.target.value })} placeholder="Your name" style={inputStyle} />
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <label style={labelStyle}>Email</label>
                      <input value={signup.email} onChange={e => patchSignup({ email: e.target.value })} type="email" placeholder="you@example.com" style={inputStyle} />
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <label style={labelStyle}>Password</label>
                      <input value={signup.password} onChange={e => patchSignup({ password: e.target.value })} type="password" placeholder="At least 6 characters" style={inputStyle} />
                    </div>
                    <div style={{ marginBottom: 26 }}>
                      <label style={labelStyle}>Confirm password</label>
                      <input value={signup.confirm} onChange={e => patchSignup({ confirm: e.target.value })} type="password" placeholder="••••••••"
                        onKeyDown={e => e.key === "Enter" && signupSendOtp()} style={inputStyle} />
                    </div>
                    <button onClick={signupSendOtp} disabled={signup.loading} className="tt-press" style={primaryBtnStyle(signup.loading)}>
                      {signup.loading && <IconSpinner size={14} color="#1a1306" />}
                      {signup.loading ? "Sending code…" : "Send verification code"}
                    </button>
                  </>
                ) : (
                  <>
                    <div style={{ marginBottom: 26 }}>
                      <label style={labelStyle}>6-digit code</label>
                      <input value={signup.otp} onChange={e => patchSignup({ otp: e.target.value.replace(/\D/g, "").slice(0, 6) })}
                        inputMode="numeric" placeholder="000000" onKeyDown={e => e.key === "Enter" && signupVerifyAndRegister()}
                        style={{ ...inputStyle, fontFamily: fontMono, fontSize: 20, letterSpacing: "6px", textAlign: "center" }} />
                    </div>
                    <button onClick={signupVerifyAndRegister} disabled={signup.loading} className="tt-press" style={primaryBtnStyle(signup.loading)}>
                      {signup.loading && <IconSpinner size={14} color="#1a1306" />}
                      {signup.loading ? "Verifying…" : "Verify & create account"}
                    </button>
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 18 }}>
                      <button onClick={() => patchSignup({ step: "form", otp: "", err: "", info: "" })} style={{ ...linkBtnStyle, display: "flex", alignItems: "center", gap: 5 }}>
                        <IconArrowLeft size={12} /> Edit details
                      </button>
                      <button onClick={signupResend} style={linkBtnStyle}>Resend code</button>
                    </div>
                  </>
                )}

                <div style={{ marginTop: 22, textAlign: "center", fontSize: 13, color: theme.textFaint }}>
                  Already have an account?{" "}
                  <button onClick={() => { setSignup({ step: "form", name: "", email: "", password: "", confirm: "", otp: "", err: "", info: "", loading: false }); setAuthMode("signin"); }} style={linkBtnStyle}>Sign in</button>
                </div>
              </>
            )}

            {/* FORGOT PASSWORD */}
            {authMode === "forgot" && (
              <>
                {forgot.err && <div className="tt-slide-in" style={errBoxStyle}>{forgot.err}</div>}
                {forgot.info && forgot.step !== "email" && <div className="tt-slide-in" style={infoBoxStyle}>{forgot.info}</div>}

                {forgot.step === "email" && (
                  <>
                    <div style={{ marginBottom: 26 }}>
                      <label style={labelStyle}>Email</label>
                      <input value={forgot.email} onChange={e => patchForgot({ email: e.target.value })} type="email" placeholder="you@example.com"
                        onKeyDown={e => e.key === "Enter" && forgotSendOtp()} style={inputStyle} />
                    </div>
                    <button onClick={forgotSendOtp} disabled={forgot.loading} className="tt-press" style={primaryBtnStyle(forgot.loading)}>
                      {forgot.loading && <IconSpinner size={14} color="#1a1306" />}
                      {forgot.loading ? "Sending code…" : "Send reset code"}
                    </button>
                  </>
                )}

                {forgot.step === "otp" && (
                  <>
                    <div style={{ marginBottom: 26 }}>
                      <label style={labelStyle}>6-digit code</label>
                      <input value={forgot.otp} onChange={e => patchForgot({ otp: e.target.value.replace(/\D/g, "").slice(0, 6) })}
                        inputMode="numeric" placeholder="000000" onKeyDown={e => e.key === "Enter" && forgotVerifyOtp()}
                        style={{ ...inputStyle, fontFamily: fontMono, fontSize: 20, letterSpacing: "6px", textAlign: "center" }} />
                    </div>
                    <button onClick={forgotVerifyOtp} disabled={forgot.loading} className="tt-press" style={primaryBtnStyle(forgot.loading)}>
                      {forgot.loading && <IconSpinner size={14} color="#1a1306" />}
                      {forgot.loading ? "Verifying…" : "Verify code"}
                    </button>
                    <div style={{ textAlign: "right", marginTop: 18 }}>
                      <button onClick={forgotResend} style={linkBtnStyle}>Resend code</button>
                    </div>
                  </>
                )}

                {forgot.step === "newpass" && (
                  <>
                    <div style={{ marginBottom: 16 }}>
                      <label style={labelStyle}>New password</label>
                      <input value={forgot.password} onChange={e => patchForgot({ password: e.target.value })} type="password" placeholder="At least 6 characters" style={inputStyle} />
                    </div>
                    <div style={{ marginBottom: 26 }}>
                      <label style={labelStyle}>Confirm new password</label>
                      <input value={forgot.confirm} onChange={e => patchForgot({ confirm: e.target.value })} type="password" placeholder="••••••••"
                        onKeyDown={e => e.key === "Enter" && forgotSubmitNewPassword()} style={inputStyle} />
                    </div>
                    <button onClick={forgotSubmitNewPassword} disabled={forgot.loading} className="tt-press" style={primaryBtnStyle(forgot.loading)}>
                      {forgot.loading && <IconSpinner size={14} color="#1a1306" />}
                      {forgot.loading ? "Updating…" : "Reset password"}
                    </button>
                  </>
                )}

                <div style={{ marginTop: 22, textAlign: "center", fontSize: 13, color: theme.textFaint }}>
                  <button onClick={() => { setForgot({ step: "email", email: "", otp: "", token: "", password: "", confirm: "", err: "", info: "", loading: false }); setAuthMode("signin"); }} style={{ ...linkBtnStyle, display: "inline-flex", alignItems: "center", gap: 5 }}>
                    <IconArrowLeft size={12} /> Back to sign in
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </>
    );
  }

  const tabBtn = (t) => ({
    padding: "9px 18px", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600,
    borderRadius: 8, background: activeTab === t ? theme.goldSoft : "transparent",
    color: activeTab === t ? theme.goldText : theme.textDim,
    letterSpacing: "0.1px",
  });

  const remainingMin = sessionExpiresAt ? Math.max(0, Math.round((sessionExpiresAt - nowTick) / 60000)) : null;

  const navTabs = [["scan","Scan"], ["dashboard","Results"]];
  if (!user.is_admin) navTabs.push(["billing","Billing"]);
  navTabs.push(["settings","Settings"]);
  if (user.is_admin) navTabs.push(["admin","Admin"]);

  const isDemoActive = !!(access && access.ok && access.is_demo);
  const isAccessExpired = !!(access && access.ok === false);
  const demoMsLeft = isDemoActive && access.demo_expires_at ? (new Date(access.demo_expires_at).getTime() - nowTick) : null;
  const subExpiringDaysLeft = (access && access.ok && access.reason === "subscription" && access.access_expires_at)
    ? Math.ceil((new Date(access.access_expires_at).getTime() - nowTick) / 86400000)
    : null;
  const showSubExpiringBanner = subExpiringDaysLeft !== null && subExpiringDaysLeft <= 3;

  /* ════════════════════════ RENDER: main app ════════════════════════ */
  return (
    <>
      <GlobalStyles />
      <div style={{ minHeight: "100vh", background: theme.bg, fontFamily: fontBody, color: theme.text }}>

        {/* Header */}
        <header style={{
          background: theme.bgRaised, height: 60,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "0 28px", borderBottom: `1px solid ${theme.border}`,
          position: "sticky", top: 0, zIndex: 100,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 22 }}>
            <button className="tt-logo-btn" onClick={() => setActiveTab("scan")} title="Go to Scan">
              <div style={{ display: "flex", alignItems: "center", gap: 9, color: theme.gold }}>
                <IconGem size={20} />
                <span style={{ fontWeight: 700, fontSize: 15, letterSpacing: "0.5px", color: theme.text, fontFamily: fontDisplay }}>
                  TalentTracker
                </span>
              </div>
            </button>
            <div style={{ width: 1, height: 22, background: theme.border }} />
            <nav style={{ display: "flex", gap: 3, background: theme.bgInset, borderRadius: 10, padding: 4 }}>
              {navTabs.map(([t, l]) => (
                <button key={t} className="tt-tab-btn" style={tabBtn(t)} onClick={() => setActiveTab(t)}>{l}</button>
              ))}
            </nav>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            {remainingMin !== null && (
              <span title="Session auto-expires 1 hour after login" style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: remainingMin <= 5 ? theme.amber : theme.textFaint, fontFamily: fontMono }}>
                <IconClock size={12} /> {remainingMin}m left
              </span>
            )}
            {!user.is_admin && access && (
              <button onClick={() => setActiveTab("billing")} className="tt-hover-lift tt-press" title="Manage your plan" style={{
                display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 700,
                padding: "6px 13px", borderRadius: 999, cursor: "pointer", border: `1px solid ${theme.border}`,
                background: accessStatusMeta(access).bg, color: accessStatusMeta(access).color,
              }}>
                <IconShield size={11} /> {accessStatusMeta(access).text}
              </button>
            )}
            <span style={{ fontSize: 13, color: theme.textDim, fontWeight: 500 }}>{user.name}</span>
            <button onClick={() => handleLogout(false)} className="tt-hover-lift tt-press"
              style={{ background: theme.bgInset, border: `1px solid ${theme.border}`, color: theme.textDim, padding: "7px 14px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
              <IconLogOut size={13} /> Sign out
            </button>
          </div>
        </header>

        {/* Access banners — persistent by design, this is the monetization nudge */}
        {!user.is_admin && isAccessExpired && (
          <div className="tt-fade-up" style={{ padding: "11px 28px", display: "flex", alignItems: "center", gap: 12, background: theme.garnetBg, borderBottom: `1px solid ${theme.border}`, fontSize: 12.5 }}>
            <IconAlertTriangle size={14} color={theme.garnet} />
            <span style={{ color: theme.garnet, fontWeight: 600, flex: 1 }}>{accessExpiredMessage(access)}</span>
            <button onClick={() => setActiveTab("billing")} className="tt-press" style={{ padding: "6px 16px", borderRadius: 8, border: "none", fontWeight: 700, fontSize: 11.5, cursor: "pointer", background: theme.garnet, color: "#2a0d0d" }}>
              Subscribe now
            </button>
          </div>
        )}
        {!user.is_admin && isDemoActive && (
          <div className="tt-fade-up" style={{ padding: "11px 28px", display: "flex", alignItems: "center", gap: 12, background: theme.goldSoft, borderBottom: `1px solid ${theme.border}`, fontSize: 12.5 }}>
            <IconClock size={13} color={theme.goldText} />
            <span style={{ color: theme.goldText, fontWeight: 600, flex: 1 }}>Free demo — {fmtDuration(demoMsLeft)} left. Subscribe anytime to keep your scans running.</span>
            <button onClick={() => setActiveTab("billing")} className="tt-press" style={{ padding: "6px 16px", borderRadius: 8, border: "none", fontWeight: 700, fontSize: 11.5, cursor: "pointer", background: theme.gold, color: "#1a1306" }}>
              View plans
            </button>
          </div>
        )}
        {!user.is_admin && !isDemoActive && !isAccessExpired && showSubExpiringBanner && (
          <div className="tt-fade-up" style={{ padding: "10px 28px", display: "flex", alignItems: "center", gap: 12, background: theme.bgInset, borderBottom: `1px solid ${theme.border}`, fontSize: 12.5 }}>
            <IconClock size={13} color={theme.textDim} />
            <span style={{ color: theme.textDim, flex: 1 }}>Your plan renews in {Math.max(subExpiringDaysLeft, 0)} day{subExpiringDaysLeft === 1 ? "" : "s"}.</span>
            <button onClick={() => setActiveTab("billing")} style={{ background: "none", border: `1px solid ${theme.border}`, color: theme.text, padding: "5px 13px", borderRadius: 8, fontSize: 11.5, fontWeight: 600, cursor: "pointer" }}>Manage plan</button>
          </div>
        )}

        <main style={{ maxWidth: (activeTab === "settings" || activeTab === "billing" || activeTab === "admin") ? "100%" : 1200, margin: "0 auto", padding: (activeTab === "settings" || activeTab === "billing" || activeTab === "admin") ? "0" : "28px 24px" }}>

          {/* ══════════════ SCAN TAB ══════════════ */}
          {activeTab === "scan" && (
            <div className="tt-fade-up" style={{ display: "grid", gridTemplateColumns: "390px 1fr", gap: 24, alignItems: "start" }}>

              {/* Left: Controls */}
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

                <div style={{ background: theme.bgRaised, border: `1px solid ${theme.border}`, borderRadius: 14, padding: 26 }}>
                  <div style={{ fontSize: 11.5, fontWeight: 700, color: theme.textDim, letterSpacing: "1.4px", textTransform: "uppercase", marginBottom: 22, fontFamily: fontMono }}>
                    Search Configuration
                  </div>

                  <TagInput label="Roles (override defaults)" tags={scanRoles} onChange={setScanRoles} suggestions={roleSugg} placeholder="Select or type a role…" onQueryChange={queryRoleSuggestions} />
                  <TagInput label="Locations (override defaults)" tags={scanCities} onChange={setScanCities} suggestions={citySugg} placeholder="Select or type a city…" onQueryChange={queryCitySuggestions} />

                  <div style={{ marginBottom: 22 }}>
                    <div style={{ fontSize: 11.5, fontWeight: 700, color: theme.textDim, letterSpacing: "1.4px", textTransform: "uppercase", marginBottom: 11, fontFamily: fontMono }}>Active Portals</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                      {prefs.sources.map(src => {
                        const m = SOURCE_META[src] || {};
                        return <Pill key={src} label={src} color={m.color} bg={m.bg} border={m.color + "33"} />;
                      })}
                      {prefs.sources.length === 0 && <span style={{ fontSize: 12, color: theme.textFaint }}>None selected — add some in Settings</span>}
                    </div>
                  </div>

                  <div style={{ marginBottom: 22, padding: "14px 16px", background: theme.bgInset, borderRadius: 10, fontSize: 12, color: theme.textDim, border: `1px solid ${theme.border}` }}>
                    <div style={{ fontWeight: 700, marginBottom: 6, color: theme.textFaint, textTransform: "uppercase", letterSpacing: "0.8px", fontSize: 10.5, fontFamily: fontMono }}>Experience Filter</div>
                    <div style={{ fontWeight: 600, marginBottom: 4, color: theme.text, fontFamily: fontMono }}>{prefs.min_exp}–{prefs.max_exp} years</div>
                    <div style={{ fontSize: 11, color: theme.textFaint }}>Senior roles & titles requiring {prefs.max_exp + 1}+ years are filtered out</div>
                  </div>

                  {prefs.has_gemini_key && (
                    <div style={{ marginBottom: 22, display: "flex", alignItems: "center", gap: 8, fontSize: 11.5, color: theme.gold, background: theme.goldSoft, border: `1px solid ${theme.border}`, borderRadius: 10, padding: "9px 13px" }}>
                      <IconSparkle size={13} /> Smart search active — Gemini is widening your role matches
                    </div>
                  )}

                  {!scanning ? (
                    <button onClick={startScan} className="tt-press"
                      disabled={!scanRoles.length && !prefs.roles.length}
                      style={{
                        width: "100%", padding: "13px", borderRadius: 10, border: "none",
                        background: (!scanRoles.length && !prefs.roles.length) ? "rgba(201,164,104,0.35)" : theme.gold,
                        color: "#1a1306", fontSize: 14, fontWeight: 700, cursor: "pointer", letterSpacing: "0.2px",
                      }}>
                      Start Scan
                    </button>
                  ) : (
                    <div style={{ display: "flex", gap: 10 }}>
                      {!paused ? (
                        <button onClick={pauseScan} className="tt-press" style={{ flex: 1, padding: "10px", background: theme.amberBg, color: theme.amber, border: `1px solid rgba(245,185,110,0.3)`, borderRadius: 10, fontWeight: 700, cursor: "pointer", fontSize: 13 }}>Pause</button>
                      ) : (
                        <button onClick={resumeScan} className="tt-press" style={{ flex: 1, padding: "10px", background: theme.emeraldBg, color: theme.emerald, border: `1px solid rgba(52,211,153,0.3)`, borderRadius: 10, fontWeight: 700, cursor: "pointer", fontSize: 13 }}>Resume</button>
                      )}
                      <button onClick={stopScan} className="tt-press" style={{ flex: 1, padding: "10px", background: theme.garnetBg, color: theme.garnet, border: `1px solid rgba(243,113,113,0.3)`, borderRadius: 10, fontWeight: 700, cursor: "pointer", fontSize: 13 }}>Stop</button>
                    </div>
                  )}

                  {scanStatus && (
                    <div className="tt-count-in" style={{
                      marginTop: 14, padding: "11px 14px", borderRadius: 10, fontSize: 12,
                      display: "flex", alignItems: "center", gap: 9,
                      background: scanDone ? theme.emeraldBg : theme.goldSoft,
                      color: scanDone ? theme.emerald : theme.goldText,
                      border: `1px solid ${scanDone ? "rgba(52,211,153,0.3)" : theme.border}`,
                    }}>
                      {scanning && !paused && <IconSpinner size={12} color={theme.goldText} />}
                      {scanDone && <IconCheck size={12} color={theme.emerald} />}
                      <span style={{ flex: 1 }}>{scanStatus}</span>
                    </div>
                  )}

                  {scanDone && emailSent && (
                    <div className="tt-count-in" style={{ marginTop: 10, padding: "9px 12px", borderRadius: 10, background: theme.emeraldBg, border: `1px solid rgba(52,211,153,0.3)`, fontSize: 12, color: theme.emerald, display: "flex", gap: 7, alignItems: "center" }}>
                      <IconCheck size={12} /> Results emailed with CSV attachment
                    </div>
                  )}
                </div>

                {(scanning || scanDone) && (
                  <div className="tt-fade-up" style={{ background: theme.bgRaised, border: `1px solid ${theme.border}`, borderRadius: 14, padding: 20 }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                      <div style={{ textAlign: "center", padding: "16px 10px", background: theme.emeraldBg, borderRadius: 10, border: `1px solid rgba(52,211,153,0.25)` }}>
                        <div style={{ fontSize: 32, fontWeight: 800, color: theme.emerald, lineHeight: 1, fontFamily: fontMono }}>{liveCount}</div>
                        <div style={{ fontSize: 10.5, color: theme.textDim, fontWeight: 700, marginTop: 5, textTransform: "uppercase", letterSpacing: "0.6px" }}>New Jobs</div>
                      </div>
                      <div style={{ textAlign: "center", padding: "16px 10px", background: theme.bgInset, borderRadius: 10, border: `1px solid ${theme.border}` }}>
                        <div style={{ fontSize: 32, fontWeight: 800, color: theme.textFaint, lineHeight: 1, fontFamily: fontMono }}>{skipCount}</div>
                        <div style={{ fontSize: 10.5, color: theme.textDim, fontWeight: 700, marginTop: 5, textTransform: "uppercase", letterSpacing: "0.6px" }}>Filtered</div>
                      </div>
                    </div>
                    {currentSource && (
                      <div style={{ marginTop: 14, fontSize: 12, color: theme.textDim, display: "flex", alignItems: "center", gap: 8 }}>
                        <IconSpinner size={11} color={theme.gold} />
                        <span>Searching <strong style={{ color: theme.text }}>{currentSource}</strong>…</span>
                      </div>
                    )}
                  </div>
                )}

                {(scanning || scanDone) && prefs.sources.length > 0 && (
                  <div className="tt-fade-up" style={{ background: theme.bgRaised, border: `1px solid ${theme.border}`, borderRadius: 14, padding: 20 }}>
                    <div style={{ fontSize: 11.5, fontWeight: 700, color: theme.textDim, letterSpacing: "1.4px", textTransform: "uppercase", marginBottom: 14, fontFamily: fontMono }}>Portal Status</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {prefs.sources.map(src => {
                        const done = sourceDone.includes(src);
                        const active = currentSource === src;
                        const count = statsBySource[src] || 0;
                        const m = SOURCE_META[src] || {};
                        return (
                          <div key={src} className={active ? "tt-shimmer-border" : ""} style={{
                            display: "flex", alignItems: "center", gap: 10,
                            padding: "9px 13px", borderRadius: 9, fontSize: 12,
                            background: done ? theme.bgInset : active ? theme.goldSoft : "transparent",
                            border: `1px solid ${active ? theme.border : done ? theme.border : "transparent"}`,
                          }}>
                            <div style={{ width: 16, height: 16, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: active ? theme.gold : done ? theme.emerald : theme.textFaint }}>
                              {done ? <IconCheck size={14} /> : active ? <IconSpinner size={12} /> : <span className="tt-pulse-dot" style={{ width: 6, height: 6, borderRadius: "50%", background: theme.textFaint, display: "inline-block" }} />}
                            </div>
                            <SourcePill source={src} meta={SOURCE_META} />
                            {done && count > 0 && <span style={{ marginLeft: "auto", fontSize: 11.5, fontWeight: 700, color: m.color || theme.textDim, fontFamily: fontMono }}>+{count}</span>}
                            {done && count === 0 && <span style={{ marginLeft: "auto", fontSize: 11, color: theme.textFaint, fontWeight: 600, fontFamily: fontMono }}>0</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Right: Live results */}
              <div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                  <div>
                    <div style={{ fontSize: 19, fontWeight: 700, color: theme.text, fontFamily: fontDisplay, letterSpacing: "-0.01em" }}>
                      Live Results
                      {liveCount > 0 && (
                        <span style={{ marginLeft: 12, background: theme.gold, color: "#1a1306", padding: "3px 13px", borderRadius: 999, fontSize: 12, fontWeight: 700 }}>{liveCount} new</span>
                      )}
                    </div>
                    {jobs.length > 0 && <div style={{ fontSize: 12, color: theme.textFaint, marginTop: 5 }}>New entries appear at the bottom</div>}
                  </div>
                  {jobs.length > 0 && (
                    <button onClick={clearJobs} className="tt-hover-lift tt-press" style={{ background: "transparent", color: theme.garnet, border: `1px solid rgba(243,113,113,0.3)`, padding: "8px 16px", borderRadius: 9, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                      Clear All
                    </button>
                  )}
                </div>

                {jobs.length === 0 && !scanning ? (
                  <div style={{ textAlign: "center", padding: "90px 24px", background: theme.bgRaised, borderRadius: 14, border: `1px solid ${theme.border}`, color: theme.textFaint }}>
                    <div style={{ marginBottom: 16, opacity: 0.5, display: "flex", justifyContent: "center" }}><IconGem size={38} /></div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: theme.textDim, marginBottom: 8 }}>No results yet</div>
                    <div style={{ fontSize: 13 }}>Configure your search and start a scan to see job listings here.</div>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 10, maxHeight: "78vh", overflowY: "auto" }}>
                    {jobs.map(j => (
                      <JobCard key={j.job_key || j.id} job={j} highlight={newJobKeys.has(j.job_key)} />
                    ))}
                  </div>
                )}

                {skipped.length > 0 && (
                  <div className="tt-fade-up" style={{ marginTop: 18, background: theme.bgRaised, border: `1px solid ${theme.border}`, borderRadius: 14, overflow: "hidden" }}>
                    <button onClick={() => setShowSkipped(p => !p)} className="tt-press" style={{
                      width: "100%", padding: "15px 17px", background: "none", border: "none",
                      cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between",
                      fontSize: 13, fontWeight: 600, color: theme.textDim,
                      borderBottom: showSkipped ? `1px solid ${theme.border}` : "none",
                    }}>
                      <span>{skipCount} roles filtered (experience / seniority mismatch)</span>
                      <span style={{ fontSize: 12, color: theme.textFaint }}>{showSkipped ? "Hide" : "Show"}</span>
                    </button>
                    {showSkipped && (
                      <div style={{ padding: "12px", display: "flex", flexDirection: "column", gap: 7, maxHeight: 320, overflowY: "auto" }}>
                        {skipped.map((s, i) => <SkippedRow key={i} item={s} />)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ══════════════ RESULTS TAB ══════════════ */}
          {activeTab === "dashboard" && (
            <div className="tt-fade-up">
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16, marginBottom: 24 }}>
                {[
                  { label: "Total Saved", value: jobs.length, color: theme.gold, bg: theme.goldSoft },
                  { label: "Unique Titles", value: [...new Set(jobs.map(j => j.title))].length, color: theme.sapphire, bg: theme.sapphireBg },
                  { label: "Locations", value: [...new Set(jobs.map(j => j.location))].length, color: theme.emerald, bg: theme.emeraldBg },
                  { label: "Portals", value: [...new Set(jobs.map(j => j.source))].length, color: theme.amber, bg: theme.amberBg },
                ].map(s => (
                  <div key={s.label} className="tt-hover-lift" style={{ background: theme.bgRaised, borderRadius: 14, padding: "22px 24px", border: `1px solid ${theme.border}`, borderTop: `2px solid ${s.color}` }}>
                    <div style={{ fontSize: 32, fontWeight: 800, color: s.color, lineHeight: 1, fontFamily: fontMono }}>{s.value}</div>
                    <div style={{ fontSize: 11.5, color: theme.textDim, fontWeight: 700, marginTop: 8, textTransform: "uppercase", letterSpacing: "0.6px" }}>{s.label}</div>
                  </div>
                ))}
              </div>

              {prefs.sources.length > 0 && (
                <div style={{ background: theme.bgRaised, borderRadius: 14, padding: "20px 24px", border: `1px solid ${theme.border}`, marginBottom: 18 }}>
                  <div style={{ fontSize: 11.5, fontWeight: 700, color: theme.textDim, letterSpacing: "1.4px", textTransform: "uppercase", marginBottom: 15, fontFamily: fontMono }}>Results by Portal</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                    {prefs.sources.map(src => {
                      const m = SOURCE_META[src] || {};
                      const n = statsBySource[src] || 0;
                      return (
                        <div key={src} style={{ display: "flex", alignItems: "center", gap: 9, padding: "9px 15px", borderRadius: 9, background: m.bg || theme.bgInset, border: `1px solid ${(m.color || theme.textDim) + "33"}` }}>
                          <span style={{ fontWeight: 700, fontSize: 12, color: m.color || theme.textDim }}>{src}</span>
                          <span style={{ background: m.color || theme.textDim, color: "#0a0b0f", borderRadius: 999, padding: "2px 10px", fontSize: 11, fontWeight: 800, fontFamily: fontMono }}>{n}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div style={{ background: theme.bgRaised, borderRadius: 14, padding: "17px 19px", border: `1px solid ${theme.border}`, marginBottom: 16, display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
                <input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Search by title, company, location…"
                  style={{ flex: 1, minWidth: 220, padding: "9px 14px", border: `1px solid ${theme.border}`, borderRadius: 9, fontSize: 13, background: theme.bgInset, color: theme.text }} />
                <select value={filterSource} onChange={e => setFilterSource(e.target.value)}
                  style={{ padding: "9px 13px", border: `1px solid ${theme.border}`, borderRadius: 9, fontSize: 13, background: theme.bgInset, color: theme.text }}>
                  <option value="all">All Portals</option>
                  {ALL_SOURCES.map(s => <option key={s}>{s}</option>)}
                </select>
                <select value={filterWorkType} onChange={e => setFilterWorkType(e.target.value)}
                  style={{ padding: "9px 13px", border: `1px solid ${theme.border}`, borderRadius: 9, fontSize: 13, background: theme.bgInset, color: theme.text }}>
                  <option value="all">All Work Types</option>
                  <option value="remote">Remote</option>
                  <option value="hybrid">Hybrid</option>
                  <option value="office">On-site</option>
                </select>
                <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 13, cursor: "pointer", fontWeight: 600, color: theme.textDim }}>
                  <input type="checkbox" checked={filterEasy} onChange={e => setFilterEasy(e.target.checked)} style={{ accentColor: theme.gold, width: 16, height: 16, cursor: "pointer" }} />
                  Quick Apply
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 13, cursor: "pointer", fontWeight: 600, color: theme.textDim }}>
                  <input type="checkbox" checked={filterRecruiter} onChange={e => setFilterRecruiter(e.target.checked)} style={{ accentColor: theme.sapphire, width: 16, height: 16, cursor: "pointer" }} />
                  Recruiter Posts
                </label>
                <span style={{ color: theme.textFaint, fontSize: 12, marginLeft: "auto", fontFamily: fontMono }}>{displayJobs.length} shown</span>
                {jobs.length > 0 && (
                  <button onClick={clearJobs} style={{ background: "transparent", color: theme.garnet, border: `1px solid rgba(243,113,113,0.3)`, padding: "7px 14px", borderRadius: 9, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                    Clear All
                  </button>
                )}
              </div>

              {displayJobs.length === 0 ? (
                <div style={{ textAlign: "center", padding: "90px 24px", background: theme.bgRaised, borderRadius: 14, border: `1px solid ${theme.border}`, color: theme.textFaint }}>
                  <div style={{ fontWeight: 700, color: theme.textDim, marginBottom: 8 }}>No jobs match your filters</div>
                  <div style={{ fontSize: 13 }}>Try adjusting the filters or run a new scan from the Scan tab.</div>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {displayJobs.map(j => (
                    <JobCard key={j.job_key || j.id} job={j} highlight={newJobKeys.has(j.job_key)} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ══════════════ BILLING TAB ══════════════ */}
          {activeTab === "billing" && !user.is_admin && (
            <div className="tt-fade-up" style={{ minHeight: "calc(100vh - 60px)", background: theme.bg, padding: "36px 0 80px" }}>
              <div style={{ maxWidth: 880, margin: "0 auto", padding: "0 28px" }}>
                <div style={{ marginBottom: 30 }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: theme.text, fontFamily: fontDisplay, letterSpacing: "-0.01em", marginBottom: 6 }}>Billing & Plan</div>
                  <div style={{ fontSize: 13, color: theme.textFaint }}>Subscribe to keep TalentTracker scanning for you beyond the free demo</div>
                </div>

                <div style={{ background: theme.bgRaised, border: `1px solid ${theme.border}`, borderRadius: 14, padding: "22px 24px", marginBottom: 22, display: "flex", alignItems: "center", gap: 16 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", background: accessStatusMeta(access).bg, color: accessStatusMeta(access).color, flexShrink: 0 }}>
                    <IconShield size={18} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: theme.text }}>{accessStatusMeta(access).text}</div>
                    <div style={{ fontSize: 12, color: theme.textFaint, marginTop: 3 }}>
                      {isAccessExpired && accessExpiredMessage(access)}
                      {isDemoActive && `Demo ends in ${fmtDuration(demoMsLeft)}`}
                      {access && access.ok && access.reason === "subscription" && access.access_expires_at && `Renews on ${fmtDate(access.access_expires_at)}`}
                      {access && access.ok && access.reason === "admin_grant" && (access.access_expires_at ? `Granted until ${fmtDate(access.access_expires_at)}` : "Granted by admin, no expiry")}
                    </div>
                  </div>
                </div>

                {billingErr && <div style={errBoxStyle}>{billingErr}</div>}
                {billingMsg && <div style={infoBoxStyle}>{billingMsg}</div>}

               <div style={{ marginBottom: 30 }}>
                 {billingPlans?.monthly ? (
                   <div
                     className="tt-hover-lift"
                     style={{
                       background: theme.bgRaised,
                       border: `1px solid ${theme.border}`,
                       borderRadius: 14,
                       padding: "26px 24px",
                     }}
                   >
                     <div
                       style={{
                         fontSize: 11.5,
                         fontWeight: 700,
                         color: theme.textDim,
                         letterSpacing: "1.2px",
                         textTransform: "uppercase",
                         marginBottom: 14,
                         fontFamily: fontMono,
                       }}
                     >
                       {billingPlans.monthly.label}
                     </div>

                     <div
                       style={{
                         display: "flex",
                         alignItems: "baseline",
                         gap: 6,
                         marginBottom: 22,
                       }}
                     >
                       <span
                         style={{
                           fontSize: 34,
                           fontWeight: 800,
                           color: theme.text,
                           fontFamily: fontMono,
                         }}
                       >
                         {fmtINR(billingPlans.monthly.amount)}
                       </span>
                       <span
                         style={{
                           fontSize: 13,
                           color: theme.textFaint,
                         }}
                       >
                         / month
                       </span>
                     </div>

                     <button
                       onClick={() => startCheckout("monthly")}
                       disabled={billingLoadingPlan === "monthly"}
                       className="tt-press"
                       style={{
                         width: "100%",
                         padding: "12px",
                         borderRadius: 10,
                         cursor: billingLoadingPlan === "monthly" ? "wait" : "pointer",
                         background: theme.gold,
                         color: "#1a1306",
                         border: "none",
                         fontWeight: 700,
                         fontSize: 13,
                         display: "flex",
                         alignItems: "center",
                         justifyContent: "center",
                         gap: 8,
                       }}
                     >
                       {billingLoadingPlan === "monthly" && (
                         <IconSpinner size={13} color="#1a1306" />
                       )}
                       {billingLoadingPlan === "monthly"
                         ? "Opening checkout…"
                         : "Subscribe Monthly"}
                     </button>
                   </div>
                 ) : (
                   <div
                     style={{
                       textAlign: "center",
                       padding: 40,
                       color: theme.textFaint,
                       fontSize: 13,
                       display: "flex",
                       alignItems: "center",
                       justifyContent: "center",
                       gap: 8,
                     }}
                   >
                     <IconSpinner size={16} color={theme.gold} />
                     Loading plan...
                   </div>
                 )}
               </div>

                <div style={{ fontSize: 11.5, fontWeight: 700, color: theme.textDim, letterSpacing: "1.4px", textTransform: "uppercase", marginBottom: 13, fontFamily: fontMono }}>Payment History</div>
                {paymentHistory.length === 0 ? (
                  <div style={{ background: theme.bgRaised, border: `1px solid ${theme.border}`, borderRadius: 14, padding: "30px 24px", textAlign: "center", color: theme.textFaint, fontSize: 13 }}>
                    No payments yet
                  </div>
                ) : (
                  <div style={{ background: theme.bgRaised, border: `1px solid ${theme.border}`, borderRadius: 14, overflow: "hidden" }}>
                    {paymentHistory.map((p, i) => (
                      <div key={p.id || i} style={{ display: "flex", alignItems: "center", gap: 14, padding: "13px 20px", borderBottom: i < paymentHistory.length - 1 ? `1px solid ${theme.border}` : "none" }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 700, fontSize: 13, color: theme.text, textTransform: "capitalize" }}>{p.plan} plan</div>
                          <div style={{ fontSize: 11.5, color: theme.textFaint, marginTop: 2 }}>{fmtDate(p.created_at)}</div>
                        </div>
                        <div style={{ fontFamily: fontMono, fontWeight: 700, fontSize: 13, color: theme.text }}>{fmtINR(p.amount)}</div>
                        <span style={{
                          fontSize: 10.5, fontWeight: 700, padding: "3px 10px", borderRadius: 999, textTransform: "uppercase",
                          background: p.status === "paid" ? theme.emeraldBg : theme.amberBg,
                          color: p.status === "paid" ? theme.emerald : theme.amber,
                        }}>{p.status}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ══════════════ SETTINGS TAB — full screen ══════════════ */}
          {activeTab === "settings" && (
            <div className="tt-fade-up" style={{ minHeight: "calc(100vh - 60px)", background: theme.bg, padding: "36px 0 80px" }}>
              <div style={{ maxWidth: 880, margin: "0 auto", padding: "0 28px" }}>
                <div style={{ marginBottom: 30 }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: theme.text, fontFamily: fontDisplay, letterSpacing: "-0.01em", marginBottom: 6 }}>Preferences & Defaults</div>
                  <div style={{ fontSize: 13, color: theme.textFaint }}>These apply to every scan unless overridden on the Scan tab</div>
                </div>

                <div style={{ background: theme.bgRaised, border: `1px solid ${theme.border}`, borderRadius: 16, padding: "36px 40px" }}>

                  <TagInput label="Default Roles" tags={prefs.roles} onChange={v => updatePref("roles", v)} suggestions={roleSugg} placeholder="Search or type a role…" onQueryChange={queryRoleSuggestions} />
                  <TagInput label="Default Locations" tags={prefs.cities} onChange={v => updatePref("cities", v)} suggestions={citySugg} placeholder="Search or type a city…" onQueryChange={queryCitySuggestions} />

                  {/* Portals */}
                  <div style={{ marginBottom: 28 }}>
                    <div style={{ fontSize: 11.5, fontWeight: 700, color: theme.textDim, letterSpacing: "1.4px", textTransform: "uppercase", marginBottom: 13, fontFamily: fontMono }}>Job Portals</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 9 }}>
                      {ALL_SOURCES.map(src => {
                        const on = prefs.sources.includes(src);
                        const m  = SOURCE_META[src] || { color: theme.textDim, bg: theme.bgInset };
                        return (
                          <button key={src} className="tt-press" onClick={() => {
                            const next = on ? prefs.sources.filter(s => s !== src) : [...prefs.sources, src];
                            updatePref("sources", next);
                          }} style={{
                            padding: "8px 17px", borderRadius: 9, cursor: "pointer",
                            fontWeight: 700, fontSize: 12,
                            border: `1px solid ${on ? m.color : theme.border}`,
                            background: on ? m.bg : theme.bgInset,
                            color: on ? m.color : theme.textFaint,
                          }}>{on && "✓ "}{src}</button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Gemini smart search */}
                  <div style={{ marginBottom: 28, padding: "20px 22px", background: theme.goldSoft, border: `1px solid ${theme.border}`, borderRadius: 12 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 10 }}>
                      <IconSparkle size={15} color={theme.gold} />
                      <div style={{ fontSize: 11.5, fontWeight: 700, color: theme.goldText, letterSpacing: "1.2px", textTransform: "uppercase", fontFamily: fontMono }}>Smart Search (Gemini)</div>
                    </div>
                    <div style={{ fontSize: 12, color: theme.textDim, marginBottom: 14, lineHeight: 1.6 }}>
                      Add a Gemini API key to widen your role matches and double-check ambiguous senior/junior calls.
                      Get a free key at{" "}
                      <a href="https://aistudio.google.com/api-keys" target="_blank" rel="noopener noreferrer" className="tt-link-underline" style={{ color: theme.gold, fontWeight: 600 }}>aistudio.google.com/api-keys</a>.
                      If this key is missing, invalid, or unreachable, scans automatically fall back to TalentTracker's built-in matching — search never breaks.
                    </div>
                    <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                      <div style={{ flex: 1, position: "relative", display: "flex", alignItems: "center" }}>
                        <span style={{ position: "absolute", left: 13, color: theme.textFaint }}><IconKey size={13} /></span>
                        <input
                          type={showGeminiKey ? "text" : "password"}
                          value={prefs.gemini_api_key}
                          onChange={e => { updatePref("gemini_api_key", e.target.value); setGeminiTestState(""); }}
                          placeholder="Paste your Gemini API key"
                          style={{ width: "100%", padding: "10px 14px 10px 36px", border: `1px solid ${theme.border}`, borderRadius: 9, fontSize: 13, background: theme.bgInset, color: theme.text, fontFamily: fontMono }}
                        />
                        <button onClick={() => setShowGeminiKey(s => !s)} style={{ position: "absolute", right: 10, background: "none", border: "none", color: theme.textFaint, fontSize: 11, cursor: "pointer", fontWeight: 600 }}>
                          {showGeminiKey ? "Hide" : "Show"}
                        </button>
                      </div>
                      <button onClick={testGeminiKey} disabled={!prefs.gemini_api_key || geminiTestState === "testing"} className="tt-press" style={{
                        padding: "10px 18px", borderRadius: 9, border: `1px solid ${theme.border}`,
                        background: theme.bgInset, color: theme.text, fontSize: 12, fontWeight: 600,
                        cursor: prefs.gemini_api_key ? "pointer" : "not-allowed", whiteSpace: "nowrap",
                        display: "flex", alignItems: "center", gap: 7,
                      }}>
                        {geminiTestState === "testing" && <IconSpinner size={12} />}
                        Test Key
                      </button>
                    </div>
                    {geminiTestState === "ok" && (
                      <div style={{ marginTop: 10, fontSize: 12, color: theme.emerald, display: "flex", alignItems: "center", gap: 6 }}><IconCheck size={12} /> {geminiTestMsg}</div>
                    )}
                    {geminiTestState === "fail" && (
                      <div style={{ marginTop: 10, fontSize: 12, color: theme.amber }}>{geminiTestMsg}</div>
                    )}
                  </div>

                  {/* Experience */}
                  <div style={{ marginBottom: 28, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                    {[["min_exp","Minimum Experience (years)"],["max_exp","Maximum Experience (years)"]].map(([k, l]) => (
                      <div key={k}>
                        <label style={{ fontSize: 11, fontWeight: 600, color: theme.textDim, letterSpacing: "1.2px", textTransform: "uppercase", display: "block", marginBottom: 9, fontFamily: fontMono }}>{l}</label>
                        <input type="number" min={0} max={30} value={prefs[k]}
                          onChange={e => updatePref(k, +e.target.value)}
                          style={{ width: "100%", padding: "10px 14px", border: `1px solid ${theme.border}`, borderRadius: 9, fontSize: 14, boxSizing: "border-box", background: theme.bgInset, color: theme.text }} />
                      </div>
                    ))}
                  </div>
                  <div style={{ marginBottom: 28, padding: "13px 15px", background: theme.amberBg, borderRadius: 10, fontSize: 12, color: theme.amber, border: `1px solid rgba(245,185,110,0.25)`, lineHeight: 1.7 }}>
                    Roles requiring more than {prefs.max_exp} years are filtered out. Senior titles (Senior, Lead, Director, VP, Manager, Principal, Staff, Architect) are automatically skipped for users with {prefs.max_exp} years or less.
                  </div>

                  {/* Work type */}
                  <div style={{ marginBottom: 28 }}>
                    <div style={{ fontSize: 11.5, fontWeight: 700, color: theme.textDim, letterSpacing: "1.4px", textTransform: "uppercase", marginBottom: 12, fontFamily: fontMono }}>Work Mode Preference</div>
                    <div style={{ display: "flex", gap: 9 }}>
                      {["any","remote","hybrid","office"].map(w => (
                        <button key={w} className="tt-press" onClick={() => updatePref("work_type", w)} style={{
                          padding: "9px 19px", borderRadius: 9, cursor: "pointer", fontWeight: 700,
                          fontSize: 12, border: `1px solid ${prefs.work_type === w ? theme.gold : theme.border}`,
                          background: prefs.work_type === w ? theme.goldSoft : theme.bgInset,
                          color: prefs.work_type === w ? theme.goldText : theme.textFaint,
                        }}>{w.charAt(0).toUpperCase() + w.slice(1)}</button>
                      ))}
                    </div>
                  </div>

                  {/* Job age */}
                  <div style={{ marginBottom: 28, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: theme.textDim, letterSpacing: "1.2px", textTransform: "uppercase", display: "block", marginBottom: 9, fontFamily: fontMono }}>Max Job Age (days)</label>
                      <input type="number" min={1} max={90} value={prefs.job_age_days}
                        onChange={e => updatePref("job_age_days", +e.target.value)}
                        style={{ width: "100%", padding: "10px 14px", border: `1px solid ${theme.border}`, borderRadius: 9, fontSize: 14, boxSizing: "border-box", background: theme.bgInset, color: theme.text }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: theme.textDim, letterSpacing: "1.2px", textTransform: "uppercase", display: "block", marginBottom: 9, fontFamily: fontMono }}>Or Max Age (minutes)</label>
                      <input type="number" min={0} value={prefs.job_age_minutes}
                        onChange={e => updatePref("job_age_minutes", +e.target.value)}
                        style={{ width: "100%", padding: "10px 14px", border: `1px solid ${theme.border}`, borderRadius: 9, fontSize: 14, boxSizing: "border-box", background: theme.bgInset, color: theme.text }} />
                    </div>
                  </div>

                  {/* Frequency */}
                  <div style={{ marginBottom: 28 }}>
                    <label style={{ fontSize: 11.5, fontWeight: 700, color: theme.textDim, letterSpacing: "1.4px", textTransform: "uppercase", display: "block", marginBottom: 12, fontFamily: fontMono }}>Scheduled Alerts</label>
                    <select value={prefs.frequency} onChange={e => updatePref("frequency", e.target.value)}
                      style={{ width: "100%", padding: "10px 14px", border: `1px solid ${theme.border}`, borderRadius: 9, fontSize: 13, marginBottom: 14, background: theme.bgInset, color: theme.text }}>
                      <option value="daily">Daily at 7:00 AM</option>
                      <option value="twice_daily">Twice Daily (7 AM, 1 PM)</option>
                      <option value="thrice_daily">Three Times Daily (7, 1, 6 PM)</option>
                      <option value="four_daily">Four Times Daily (7, 11 AM, 3, 7 PM)</option>
                      <option value="weekly">Weekly on Monday at 7 AM</option>
                      <option value="custom">Custom Time</option>
                    </select>
                    {prefs.frequency === "custom" && (
                      <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                        <div style={{ flex: 1 }}>
                          <label style={{ fontSize: 10.5, fontWeight: 600, color: theme.textFaint, display: "block", marginBottom: 7, fontFamily: fontMono }}>TIME (HH:MM)</label>
                          <input type="time" value={customFreqTime} onChange={e => setCustomFreqTime(e.target.value)}
                            style={{ width: "100%", padding: "9px 13px", border: `1px solid ${theme.border}`, borderRadius: 9, fontSize: 13, background: theme.bgInset, color: theme.text }} />
                        </div>
                        <button onClick={() => updatePref("custom_times", [...(prefs.custom_times || []), customFreqTime].filter((v, i, a) => a.indexOf(v) === i))} className="tt-press"
                          style={{ padding: "9px 18px", background: theme.gold, color: "#1a1306", border: "none", borderRadius: 9, fontWeight: 700, fontSize: 12, cursor: "pointer", whiteSpace: "nowrap" }}>
                          Add Time
                        </button>
                      </div>
                    )}
                    {prefs.custom_times && prefs.custom_times.length > 0 && (
                      <div style={{ marginTop: 13, display: "flex", flexWrap: "wrap", gap: 7 }}>
                        {prefs.custom_times.map(t => (
                          <div key={t} className="tt-count-in" style={{ display: "flex", alignItems: "center", gap: 7, background: theme.goldSoft, color: theme.goldText, padding: "6px 11px", borderRadius: 8, fontSize: 11.5, fontWeight: 700, fontFamily: fontMono }}>
                            {t}
                            <button onClick={() => updatePref("custom_times", prefs.custom_times.filter(x => x !== t))} style={{ background: "none", border: "none", cursor: "pointer", color: theme.textFaint, fontSize: 14 }}>×</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Toggles */}
                  <div style={{ marginBottom: 32, display: "flex", flexDirection: "column", gap: 12 }}>
                    {[
                      { key: "only_early_applicant", label: "Early Applicant Only", desc: "Show only roles where you'd be among the first to apply" },
                      { key: "easy_apply_only", label: "Quick Apply Only", desc: "Only roles with one-click or easy apply option" },
                      { key: "notify_email", label: "Email Notifications", desc: `Send results to ${user.email} with a CSV attachment after each scan` },
                    ].map(({ key, label, desc }) => (
                      <label key={key} className="tt-hover-lift" style={{
                        display: "flex", alignItems: "center", gap: 14, cursor: "pointer",
                        padding: "15px 17px", borderRadius: 10, background: theme.bgInset,
                        border: `1px solid ${prefs[key] ? theme.border : "transparent"}`,
                      }}>
                        <input type="checkbox" checked={!!prefs[key]} onChange={e => updatePref(key, e.target.checked)}
                          style={{ width: 18, height: 18, accentColor: theme.gold, cursor: "pointer", flexShrink: 0 }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 700, fontSize: 13, color: theme.text }}>{label}</div>
                          <div style={{ fontSize: 12, color: theme.textFaint, marginTop: 3 }}>{desc}</div>
                        </div>
                      </label>
                    ))}
                  </div>

                  {/* Save */}
                  <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                    <button onClick={savePrefs} disabled={prefsSaving || !prefsDirty} className="tt-press" style={{
                      padding: "12px 30px",
                      background: !prefsDirty || prefsSaving ? theme.bgInset : theme.gold,
                      color: !prefsDirty || prefsSaving ? theme.textFaint : "#1a1306",
                      border: !prefsDirty || prefsSaving ? `1px solid ${theme.border}` : "none",
                      borderRadius: 10, fontSize: 13, fontWeight: 700,
                      cursor: !prefsDirty || prefsSaving ? "not-allowed" : "pointer",
                    }}>{prefsSaving ? "Saving…" : "Save Preferences"}</button>
                    {prefsDirty && !prefsSaving && <span style={{ fontSize: 12, color: theme.amber, fontWeight: 600 }}>Unsaved changes</span>}
                    {prefsMsg && (
                      <span style={{ fontSize: 13, fontWeight: 700, color: prefsMsg.includes("saved") ? theme.emerald : theme.garnet }}>{prefsMsg}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ══════════════ ADMIN TAB ══════════════ */}
          {activeTab === "admin" && user.is_admin && (
            <div className="tt-fade-up" style={{ minHeight: "calc(100vh - 60px)", background: theme.bg, padding: "36px 0 80px" }}>
              <div style={{ maxWidth: 1180, margin: "0 auto", padding: "0 28px" }}>
                <div style={{ marginBottom: 26 }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: theme.text, fontFamily: fontDisplay, letterSpacing: "-0.01em", marginBottom: 6 }}>Admin</div>
                  <div style={{ fontSize: 13, color: theme.textFaint }}>Manage users, access, and revenue</div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16, marginBottom: 26 }}>
                  {[
                    { label: "Total Users", value: adminStats ? adminStats.total_users : "—", color: theme.gold },
                    { label: "Total Jobs Found", value: adminStats ? adminStats.total_jobs : "—", color: theme.sapphire },
                    { label: "Paid Payments", value: adminStats ? adminStats.paid_payments : "—", color: theme.emerald },
                    { label: "Revenue", value: adminStats ? `₹${Number(adminStats.total_revenue_inr).toLocaleString("en-IN")}` : "—", color: theme.amber },
                  ].map(s => (
                    <div key={s.label} className="tt-hover-lift" style={{ background: theme.bgRaised, borderRadius: 14, padding: "20px 22px", border: `1px solid ${theme.border}`, borderTop: `2px solid ${s.color}` }}>
                      <div style={{ fontSize: 26, fontWeight: 800, color: s.color, lineHeight: 1, fontFamily: fontMono }}>{s.value}</div>
                      <div style={{ fontSize: 11, color: theme.textDim, fontWeight: 700, marginTop: 7, textTransform: "uppercase", letterSpacing: "0.6px" }}>{s.label}</div>
                    </div>
                  ))}
                </div>

                {adminMsg && <div style={infoBoxStyle}>{adminMsg}</div>}

                <div style={{ background: theme.bgRaised, border: `1px solid ${theme.border}`, borderRadius: 14, marginBottom: 26, overflow: "hidden" }}>
                  <div style={{ padding: "16px 20px", borderBottom: `1px solid ${theme.border}`, display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ fontSize: 11.5, fontWeight: 700, color: theme.textDim, letterSpacing: "1.2px", textTransform: "uppercase", fontFamily: fontMono, display: "flex", alignItems: "center", gap: 7, whiteSpace: "nowrap" }}>
                      <IconUsers size={13} /> Users ({adminUsers.length})
                    </div>
                    <input value={adminSearch} onChange={e => setAdminSearch(e.target.value)} placeholder="Search name or email…"
                      style={{ flex: 1, padding: "7px 12px", border: `1px solid ${theme.border}`, borderRadius: 8, fontSize: 12.5, background: theme.bgInset, color: theme.text, maxWidth: 280 }} />
                    <button onClick={() => setShowAddUser(s => !s)} className="tt-press" style={{ padding: "7px 15px", background: theme.gold, color: "#1a1306", border: "none", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
                      {showAddUser ? "Cancel" : "+ Add user"}
                    </button>
                  </div>

                  {showAddUser && (
                    <div className="tt-count-in" style={{ padding: "16px 20px", borderBottom: `1px solid ${theme.border}`, background: theme.bgInset, display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
                      <div style={{ flex: 1, minWidth: 140 }}>
                        <label style={{ ...labelStyle, marginBottom: 6 }}>Name</label>
                        <input value={newUserForm.name} onChange={e => setNewUserForm(f => ({ ...f, name: e.target.value }))} style={{ ...inputStyle, padding: "8px 12px" }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 180 }}>
                        <label style={{ ...labelStyle, marginBottom: 6 }}>Email</label>
                        <input value={newUserForm.email} onChange={e => setNewUserForm(f => ({ ...f, email: e.target.value }))} type="email" style={{ ...inputStyle, padding: "8px 12px" }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 140 }}>
                        <label style={{ ...labelStyle, marginBottom: 6 }}>Password</label>
                        <input value={newUserForm.password} onChange={e => setNewUserForm(f => ({ ...f, password: e.target.value }))} type="text" style={{ ...inputStyle, padding: "8px 12px" }} />
                      </div>
                      <button onClick={submitNewUser} disabled={newUserLoading} className="tt-press" style={{ padding: "9px 18px", background: theme.gold, color: "#1a1306", border: "none", borderRadius: 9, fontWeight: 700, fontSize: 12.5, cursor: "pointer", whiteSpace: "nowrap" }}>
                        {newUserLoading ? "Creating…" : "Create"}
                      </button>
                      {newUserErr && <div style={{ width: "100%", color: theme.garnet, fontSize: 12, fontWeight: 600 }}>{newUserErr}</div>}
                    </div>
                  )}

                  <div style={{ display: "grid", gridTemplateColumns: "1.3fr 1.7fr 0.9fr 1fr 0.9fr", padding: "10px 20px", fontSize: 10.5, fontWeight: 700, color: theme.textFaint, textTransform: "uppercase", letterSpacing: "0.6px", fontFamily: fontMono, borderBottom: `1px solid ${theme.border}` }}>
                    <div>Name</div><div>Email</div><div>Status</div><div>Expires</div><div style={{ textAlign: "right" }}>Actions</div>
                  </div>
                  <div style={{ maxHeight: 420, overflowY: "auto" }}>
                    {adminUsers
                      .filter(u => !adminSearch || (u.name || "").toLowerCase().includes(adminSearch.toLowerCase()) || (u.email || "").toLowerCase().includes(adminSearch.toLowerCase()))
                      .map(u => {
                        const meta = accessStatusMeta(u.access);
                        const expiry = (u.access && (u.access.access_expires_at || u.access.demo_expires_at)) || null;
                        return (
                          <div key={u.id} style={{ display: "grid", gridTemplateColumns: "1.3fr 1.7fr 0.9fr 1fr 0.9fr", padding: "12px 20px", fontSize: 12.5, alignItems: "center", borderBottom: `1px solid ${theme.border}` }}>
                            <div style={{ color: theme.text, fontWeight: 600 }}>{u.name || "—"}</div>
                            <div style={{ color: theme.textDim }}>{u.email}</div>
                            <div><span style={{ fontSize: 10.5, fontWeight: 700, padding: "3px 9px", borderRadius: 999, background: meta.bg, color: meta.color }}>{meta.text}</span></div>
                            <div style={{ color: theme.textFaint, fontSize: 11.5 }}>{expiry ? fmtDate(expiry) : "—"}</div>
                            <div style={{ display: "flex", justifyContent: "flex-end", gap: 6 }}>
                              {u.id !== user.id && (
                                <>
                                  <button onClick={() => { setGrantTarget(u); setGrantDays(30); }} className="tt-press" style={{ padding: "5px 11px", background: theme.bgInset, border: `1px solid ${theme.border}`, color: theme.text, borderRadius: 7, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>Manage</button>
                                  <button onClick={() => deleteUserAdmin(u)} className="tt-press" title="Delete user" style={{ padding: "5px 8px", background: "transparent", border: `1px solid rgba(243,113,113,0.3)`, color: theme.garnet, borderRadius: 7, cursor: "pointer" }}>
                                    <IconTrash size={12} />
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>

                  {grantTarget && (
                    <div className="tt-count-in" style={{ padding: "18px 20px", borderTop: `1px solid ${theme.border}`, background: theme.goldSoft }}>
                      <div style={{ fontSize: 12.5, fontWeight: 700, color: theme.text, marginBottom: 12 }}>
                        Managing access — {grantTarget.name || grantTarget.email}
                      </div>
                      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                        <input type="number" min={0} value={grantDays} onChange={e => setGrantDays(+e.target.value)}
                          style={{ width: 90, padding: "8px 12px", border: `1px solid ${theme.border}`, borderRadius: 8, fontSize: 13, background: theme.bgInset, color: theme.text }} />
                        <span style={{ fontSize: 12, color: theme.textDim }}>days (0 = no expiry)</span>
                        <button onClick={() => submitGrant("grant")} disabled={grantLoading} className="tt-press" style={{ padding: "8px 16px", background: theme.gold, color: "#1a1306", border: "none", borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: "pointer" }}>Grant</button>
                        <button onClick={() => submitGrant("revoke")} disabled={grantLoading} className="tt-press" style={{ padding: "8px 16px", background: "transparent", color: theme.garnet, border: `1px solid rgba(243,113,113,0.3)`, borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: "pointer" }}>Revoke</button>
                        <button onClick={() => setGrantTarget(null)} style={{ background: "none", border: "none", color: theme.textFaint, fontSize: 12, cursor: "pointer", fontWeight: 600 }}>Cancel</button>
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ fontSize: 11.5, fontWeight: 700, color: theme.textDim, letterSpacing: "1.4px", textTransform: "uppercase", marginBottom: 13, fontFamily: fontMono }}>Recent Payments</div>
                {adminPayments.length === 0 ? (
                  <div style={{ background: theme.bgRaised, border: `1px solid ${theme.border}`, borderRadius: 14, padding: "30px 24px", textAlign: "center", color: theme.textFaint, fontSize: 13 }}>
                    No payments yet
                  </div>
                ) : (
                  <div style={{ background: theme.bgRaised, border: `1px solid ${theme.border}`, borderRadius: 14, overflow: "hidden" }}>
                    {adminPayments.slice(0, 50).map((p, i) => (
                      <div key={p.id || i} style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 20px", borderBottom: i < adminPayments.length - 1 ? `1px solid ${theme.border}` : "none", fontSize: 12.5 }}>
                        <div style={{ flex: 1.4 }}>
                          <div style={{ fontWeight: 700, color: theme.text }}>{p.user_name || "—"}</div>
                          <div style={{ fontSize: 11, color: theme.textFaint }}>{p.user_email}</div>
                        </div>
                        <div style={{ flex: 1, color: theme.textDim, textTransform: "capitalize" }}>{p.plan}</div>
                        <div style={{ flex: 1, fontFamily: fontMono, fontWeight: 700, color: theme.text }}>{fmtINR(p.amount)}</div>
                        <span style={{ fontSize: 10.5, fontWeight: 700, padding: "3px 10px", borderRadius: 999, textTransform: "uppercase", background: p.status === "paid" ? theme.emeraldBg : theme.amberBg, color: p.status === "paid" ? theme.emerald : theme.amber }}>{p.status}</span>
                        <div style={{ flex: 1, textAlign: "right", color: theme.textFaint, fontSize: 11.5 }}>{fmtDate(p.created_at)}</div>
                      </div>
                    ))}
                  </div>
                )}

                {adminLoading && (
                  <div style={{ textAlign: "center", padding: 20, color: theme.textFaint, fontSize: 12.5, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                    <IconSpinner size={13} color={theme.gold} /> Refreshing…
                  </div>
                )}
              </div>
            </div>
          )}

        </main>
      </div>
    </>
  );
}